<template>
  <div class="tw-pb-48 tw-mx-16">
    <h1 class="tw-text-5xl lg:tw-text-7xl tw-font-bold tw-mb-12 tw-text-center">Your order has been received!</h1>

    <template v-if="basketStore.order && detailsLoaded">
      <div class="xl:tw-w-1/3 lg:tw-w-1/2 tw-mx-auto">
        <h4 class="tw-text-4xl tw-font-black tw-mb-12 tw-text-center">Your Order Details</h4>

        <p class="tw-mb-12 tw-text-center">
          {{handoffText}}          
        </p>

        <table class="tw-table-fixed tw-w-full tw-mb-16">
          <tbody>
            <tr>
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Order Type
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                {{basketStore.order.friendlyDeliveryMode()}}
              </td>
            </tr>

            <tr>
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Order ID
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                {{basketStore.order.oloid}}
              </td>
            </tr>

            <tr>
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Subtotal
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                ${{basketStore.order.subtotal ? basketStore.order.subtotal.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00' }}
              </td>
            </tr>

            <tr v-if="basketStore.order && basketStore.order.tip">
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Tip
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                ${{basketStore.order && basketStore.order.tip ? basketStore.order.tip.toLocaleString("en-US", {minimumFractionDigits: 2}) : '0.00'}}
              </td>
            </tr>

            <tr v-if="basketStore.order && basketStore.order.customerhandoffcharge">
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Delivery Fee
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                ${{basketStore.order && basketStore.order.customerhandoffcharge ? basketStore.order.customerhandoffcharge.toLocaleString("en-US", {minimumFractionDigits: 2}) : '0.00'}}
              </td>
            </tr>

            <tr>
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Discounts
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                <span v-if="basketStore.order.discount && basketStore.order.discount > 0">-</span>
                ${{basketStore.order.discount ? basketStore.order.discount.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00' }}
              </td>
            </tr>

            <tr>
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Tax
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                ${{basketStore.order.totalTaxes() ? (basketStore.order.totalTaxes()).toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}}
              </td>
            </tr>
        
            <tr>
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                Total
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                ${{basketStore.order.total ? basketStore.order.total.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00' }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="tw-my-16">
          <h4 class="tw-text-3xl tw-font-bold tw-mb-12">Items Ordered</h4>
          <bagProduct
            class="tw-w-full tw-mb-4 tw-bg-white tw-shadow-xl tw-rounded-2xl"
            v-for="product in basketStore.order.products"
            :actions="false"
            :size="'confirmation'"
            :key="'bp-'+product.chainproductid.toString()"
            :product="product" 
            :recipient="product.recipient"/>
        </div>

        <div class="tw-mt-16 tw-mb-8 tw-text-center">
          <p v-if="canBeEditedOrCancelled">
            <Strong>This order can be edited until {{ editOrderCutoffTime ? editOrderCutoffTime : ' it starts being prepared' }}. </Strong><br /><br />When you edit an order, the original order will still be prepared by the restaurant without changes unless the new order is placed. You must place the new order for the original order to be canceled.
          </p>
          <p v-else>This order cannot be edited. To make changes to your order, please cancel this order, and re-order your order from the "Previous Orders" section, or contact a catering representative at 1.855.MYPIADA</p>
        </div>  
        
        <div class="tw-my-16">
          <TwButton 
            cssClass = "tw-block tw-mx-auto tw-mb-16 hover:tw-bg-black hover:tw-text-white hover:tw-border-black tw-border-orange-500 tw-border-2 tw-bg-orange-500 tw-rounded-full tw-text-white tw-uppercase tw-font-bold tw-px-8 tw-py-4 tw-transition-all tw-duration-200" 
            @click="editOrderClick" 
            :isDisabled="!canBeEditedOrCancelled"
            buttonText="Edit Order"/>
        </div>

        <div class="tw-my-16">
          <TwButton 
            cssClass="tw-block tw-mx-auto tw-mb-16 hover:tw-bg-red-500 hover:tw-text-white hover:tw-border-red-500 tw-border-gray-400 tw-border-2 tw-rounded-full tw-text-gray-400 tw-uppercase tw-font-bold tw-px-8 tw-py-4 tw-transition-all tw-duration-200" 
            @emit-click="cancelOrder" 
            :isDisabled="!canBeEditedOrCancelled" 
            buttonText="Cancel Order"/>
        </div>

        <div class="tw-mt-16 tw-mb-8 tw-text-center">
          <p v-if="canBeEditedOrCancelled">
            Catering orders that are canceled with less than a three-hour notice will be charged a cancelation fee of 50% of the order total.
          </p>
        </div>  

      </div>
    </template>
    <template v-else>
      <div class="xl:tw-w-1/3 lg:tw-w-1/2 tw-mx-auto">

        <SkeletonLoader class="tw-w-3/4 tw-h-16 tw-mb-8" />
        <table class="tw-table-fixed tw-w-full tw-mb-16">
          <tbody>
            <tr 
              v-for="idx in 6" 
              :key="'loader-'+idx">
              <td class="tw-text-xl tw-uppercase tw-border-b tw-border-gray-300 tw-py-4 tw-pr-4">
                <SkeletonLoader class="tw-w-3/4 tw-h-8" />
              </td>
              <td class="tw-text-xl tw-text-right tw-border-b tw-border-gray-300 tw-py-4 tw-pl-4">
                <SkeletonLoader class="tw-inline-block tw-w-1/3 tw-h-8" />
              </td>
            </tr>
          </tbody>
        </table>

        <div class="tw-text-center tw-border-b tw-border-gray-300 tw-mb-12">
          <SkeletonLoader class="tw-w-3/4 tw-h-16 tw-mb-12" />
        </div>

        <SkeletonLoader class="tw-w-full tw-h-32 tw-mb-8" />
        <SkeletonLoader class="tw-w-full tw-h-32 tw-mb-8" />
      </div>
    </template>

  </div>
</template>

<script setup lang="ts">
  import {
    defineComponent, reactive, computed, onMounted, ref,
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { DateTime } from "luxon";

  // Data
  import type Location from '../../classes/Location';
  import type OrderDetails from '../../classes/OrderDetails';
  import { useBasketStore } from '../../stores/basket';
  import { useLocationsStore } from '../../stores/locations';
  import { useFlashMessageStore } from '../../stores/flashMessages';
  import OrdersService from '../../services/Orders';
  import type OrderDetail from '../../classes/OrderDetails';


  import type FlashMessage from '../../types/FlashMessage'

  // Components
  import TwButton from '../../components/TwButton.vue'
  import BagProduct from '../bag/_BagProduct.vue'
  import SkeletonLoader from '../../components/SkeletonLoader.vue';

  const basketStore = useBasketStore();
  const locationsStore = useLocationsStore();
  const flashMessageStore = useFlashMessageStore();
  const router = useRouter();

  const route = useRoute();

  const location = ref({} as Location);
  const detailsLoaded = ref(false);

  const getOrderDetails = computed(() => {
    // return menuStore.getMenu;
  });

  const orderTimeWantedText = computed(() => {
    if (basketStore.order && basketStore.order!.readytime) {
      const dt = DateTime.fromFormat(basketStore.order!.readytime!, "yyyyMMdd HH:mm");
      let weekday = dt.toFormat('cccc');
      let day = dt.toFormat('MMMM d');
      let time = dt.toFormat('h:mm a')

      return weekday + ', ' + day + ' at ' + time
    } else {
      return ''
    }
  });

  const canBeEditedOrCancelled = computed(() => {
    if (basketStore.order && basketStore.order!.status.toLowerCase() == "scheduled") {
      return true;
    } else {
      return false;
    }
  })

  onMounted(() => {
    if (localStorage.getItem('orderId')) {
      basketStore.getOrder(localStorage.getItem('orderId')!).then((order: OrderDetails) => {
        basketStore.order! = order;
        detailsLoaded.value = true;
      })
    } else if (basketStore.order != null) {
      basketStore.getOrder(localStorage.getItem('orderId')!).then((order: OrderDetails) => {
        basketStore.order! = order;
        detailsLoaded.value = true;
      })
    }
  })

  const handoffText = computed(() => {
    let str = "";
    if (basketStore.order) {
      if (basketStore.order!.deliverymode == 'dispatch') {
        str += "Your order will be delivered ";
      } else {
        str += "Pick up your catering order on ";
      }

      str += orderTimeWantedText.value;
      str += " from "
      str += basketStore.order!.vendorname ? basketStore.order!.vendorname.replace('Piada', '') : ''
    }

    return str;
  })


  const addToFaves = (() => {
    // pop a modal requesting favorite name
  })

  const cancelOrder = (() => {
    // Prompt user to make sure they want to cancel
    basketStore.cancelOrder().then(() => {
      const message = {
        id: Math.round(Date.now() / 1000),
        messageType: "success",
        text: "Your order has been canceled.",
        headline: "Order Updated",
        delay: 0, // ms
        icon: "",
        cssClass: "",
      } as FlashMessage;
      flashMessageStore.addMessage(message);
    })
  })

  const editOrderClick = (() => {
  const order = basketStore.order;
  if (!order || !order.id) {
    const message = {
      id: Math.round(Math.random() * 10000000),
      messageType: "danger",
      text: "No valid order found to edit.",
      headline: "Unable to Edit Order",
      delay: 5000,
    } as FlashMessage;
    flashMessageStore.addMessage(message);
    return;
  }

  basketStore.editOrder(order.id)
    .then(() => {
      basketStore.editing = true;
      const message = {
        id: Math.round(Math.random() * 10000000),
        messageType: "success",
        text: "You are now editing an upcoming order...",
        delay: 5000,
      } as FlashMessage;
      flashMessageStore.addMessage(message);
      router.push('/menu');
    })
    .catch((error) => {
      if (error.response?.data?.message) {
        const message = {
            id: Math.round(Math.random() * 10000000),
            messageType: "danger",
            text: error.response.data.message,
            headline: "Unable to Edit Order",
            delay: 5000, // ms
            icon: "",
            cssClass: "",
          } as FlashMessage;
          flashMessageStore.addMessage(message);
      }
    });
});

const editOrderCutoffTime = computed(() => {
    let cutoff = ""
    if (basketStore.order && basketStore.order.edit_cutoff_time) {
      let cutoffTime = basketStore.order.edit_cutoff_time;
      let dt = DateTime.fromISO(cutoffTime);
      cutoff = dt.toFormat('cccc') + ', ' + dt.toFormat('LLLL d') + nth(dt.day) + ' at ' + dt.toFormat('h:mma')
    }

    return cutoff
  })

  const nth = (number: number) => {
  if (number > 3 && number < 21) return "th";
  switch (number % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};
</script>
